import { useEffect, useRef } from "react"
import './AsciiMeshV2.css'

export function AsciiMeshV2() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const container = canvas.parentElement as HTMLElement | null
    if (!container) return

    const isMobile = window.matchMedia("(max-width: 768px)").matches
    const pixelRatio = Math.min(window.devicePixelRatio || 1, isMobile ? 1.2 : 2)
    let logicalWidth = 0
    let logicalHeight = 0

    const resize = () => {
      const rect = container.getBoundingClientRect()
      const nextWidth = Math.max(1, Math.floor(rect.width))
      const nextHeight = Math.max(1, Math.floor(rect.height))

      if (nextWidth === logicalWidth && nextHeight === logicalHeight) return

      logicalWidth = nextWidth
      logicalHeight = nextHeight
      canvas.width = Math.floor(logicalWidth * pixelRatio)
      canvas.height = Math.floor(logicalHeight * pixelRatio)
      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
    }
    resize()
    window.addEventListener("resize", resize, { passive: true })

    const chars = ["░", "▒", "▓", "█", "+", "×", "·", "∴", "∷", "≡"]
    let time = 0
    let animationFrameId: number | null = null
    let lastFrameTime = performance.now()
    let shouldAnimate = true
    let isInViewport = true
    const targetFPS = isMobile ? 18 : 30
    const frameInterval = 1000 / targetFPS

    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        isInViewport = entry.isIntersecting
        shouldAnimate = isInViewport && document.visibilityState === "visible"
      },
      { threshold: 0.1 }
    )
    visibilityObserver.observe(container)

    const onVisibilityChange = () => {
      shouldAnimate = isInViewport && document.visibilityState === "visible"
    }
    document.addEventListener("visibilitychange", onVisibilityChange)

    const animate = (currentTime: number) => {
      const elapsed = currentTime - lastFrameTime

      if (!shouldAnimate) {
        lastFrameTime = currentTime
        animationFrameId = requestAnimationFrame(animate)
        return
      }
      
      if (elapsed >= frameInterval) {
        ctx.fillStyle = "rgb(0, 0, 0)"
        ctx.fillRect(0, 0, logicalWidth, logicalHeight)

        time += isMobile ? 0.008 : 0.01

        const charWidth = isMobile ? 12 : 10
        const charHeight = isMobile ? 18 : 16
        const cols = Math.floor(logicalWidth / charWidth)
        const rows = Math.floor(logicalHeight / charHeight)

        ctx.font = "12px monospace"

      // Layer 1: Purple/cyan diagonal wave background
      for (let i = 0; i < rows; i++) {
        for (let j = 0; j < cols; j++) {
          const x = j * charWidth
          const y = i * charHeight

          // Diagonal wave pattern
          const diagonal = Math.sin((i + j) * 0.15 + time * 0.5) * Math.cos((i - j) * 0.1 - time * 0.3)
          const charIndex = Math.floor((diagonal + 1) * 0.5 * chars.length)

          // // Purple-cyan gradient based on position
          // const hue = 200 + diagonal * 40 // Range from cyan to purple
          // const alpha = 0.15 + diagonal * 0.15

          // ctx.fillStyle = `hsla(${hue}, 70%, 50%, ${alpha})`
          // ctx.fillText(chars[charIndex % chars.length], x, y)
        // 225 is a deep, pure blue. Adding diagonal * 55 reaches 280 (Vivid Purple).
const hue = 225 + (diagonal * 5); 

// Keep saturation high for that neon vibe
// Lightness at 55% ensures the purple doesn't look too washed out
const alpha = 0.2 + (diagonal * 0.2);

ctx.fillStyle = `hsla(${hue}, 95%, 55%, ${alpha})`;
ctx.fillText(chars[charIndex % chars.length], x, y);
        }
      }

      // Layer 2: Flowing ribbon shapes
      const ribbonCount = 3
      for (let r = 0; r < ribbonCount; r++) {
        const ribbonPhase = time * 0.3 + r * (Math.PI * 2) / ribbonCount

        for (let i = 0; i < rows; i++) {
          for (let j = 0; j < cols; j++) {
            const x = j * charWidth
            const y = i * charHeight

            // Create flowing ribbon pattern
            const ribbonCenterY = logicalHeight / 2 + Math.sin(ribbonPhase + j * 0.02) * logicalHeight * 0.3
            const ribbonWidth = 80 + Math.sin(time + j * 0.05) * 40
            const distFromRibbon = Math.abs(y - ribbonCenterY)

            if (distFromRibbon < ribbonWidth) {
              const ribbonIntensity = 1 - distFromRibbon / ribbonWidth
              const flow = Math.sin(j * 0.1 - time * 2 + r) * Math.cos(i * 0.05 + time)
              const charIndex = Math.floor((flow + 1) * 0.5 * chars.length)

              // Magenta/pink flowing ribbons
              const hue = 280 + flow * 30
              const alpha = ribbonIntensity * 0.5

              ctx.fillStyle = `hsla(${hue}, 80%, 70%, ${alpha})`
              ctx.fillText(chars[charIndex % chars.length], x, y)
            }
          }
        }
      }

      // Layer 3: Geometric crystal-like shapes
      const crystalCount = 4
      for (let c = 0; c < crystalCount; c++) {
        const crystalX = (logicalWidth / (crystalCount + 1)) * (c + 1) + Math.sin(time * 0.4 + c) * 50
        const crystalY = logicalHeight / 2 + Math.cos(time * 0.6 + c * 1.5) * 100

        for (let i = 0; i < rows; i++) {
          for (let j = 0; j < cols; j++) {
            const x = j * charWidth
            const y = i * charHeight

            const dx = x - crystalX
            const dy = y - crystalY

            // Create hexagonal/diamond pattern
            const angle = Math.atan2(dy, dx)
            const dist = Math.sqrt(dx * dx + dy * dy)
            const hexPattern = Math.abs(Math.sin(angle * 3)) // 6-sided pattern
            const maxDist = 60 + Math.sin(time + c) * 20

            if (dist < maxDist && hexPattern > 0.3) {
              const intensity = 1 - dist / maxDist
              const noise = Math.sin(dist * 0.1 + time * 2)
              const charIndex = Math.floor((noise + 1) * 0.5 * chars.length)

              // Teal/cyan crystals
              const alpha = intensity * hexPattern * 0.6

              ctx.fillStyle = `rgba(210, 100, 255, ${alpha})`
              ctx.fillText(chars[charIndex % chars.length], x, y)
            }
          }
        }
      }

      // Layer 4: Red/Orange accent lines (kept from original)
      for (let i = 0; i < 5; i++) {
        const lineY = (Math.sin(time + i) * 0.5 + 0.5) * logicalHeight
        const lineStart = Math.floor(lineY / charHeight)

        for (let j = 0; j < cols; j++) {
          const x = j * charWidth
          const y = lineStart * charHeight

          const wave = Math.sin(j * 0.2 + time * 2 + i)
          ctx.fillStyle = `rgba(50, ${220 + wave * 50}, 255, ${0.4 + wave * 0.2})`;
          // High Green + High Blue = Bright Cyan
// ctx.fillStyle = `rgba(0, ${180 + wave * 75}, 255, ${0.4 + wave * 0.2})`;
          // ctx.fillStyle = `rgba(255, ${100 + wave * 50}, 50, ${0.4 + wave * 0.2})`
          ctx.fillText(chars[Math.floor(Math.random() * chars.length)], x, y)
        }
      }

        lastFrameTime = currentTime
      }

      animationFrameId = requestAnimationFrame(animate)
    }
    animationFrameId = requestAnimationFrame(animate)

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }
      visibilityObserver.disconnect()
      document.removeEventListener("visibilitychange", onVisibilityChange)
      window.removeEventListener("resize", resize)
    }
  }, [])

  return (
    <section className="ascii-mesh-v2-container">
      <canvas ref={canvasRef} className="ascii-mesh-v2-canvas" />
    </section>
  )
}
